export const RECOVERY_PROVIDER_CONTRACT_VERSION = 'recovery-provider.v1' as const;

export type RecoveryProviderId = 'privy' | 'unsupported';
export type RecoveryInputKind = 'recovery-encryption-key' | 'provider-managed' | 'phrase' | 'none';
export type RecoverySpikeStatus = 'proved' | 'split-deferred';
export type RecoveryAttemptStatus = 'succeeded' | 'failed' | 'deferred';

export interface RecoveryProviderCapability {
  providerId: RecoveryProviderId;
  inputKind: RecoveryInputKind;
  phraseBased: boolean;
  canRecoverWalletIdentity: boolean;
  canProveDeterministicAppKeys: boolean;
  notes: string[];
}

export interface RecoverySpikeRecord {
  contractVersion: typeof RECOVERY_PROVIDER_CONTRACT_VERSION;
  status: RecoverySpikeStatus;
  providerCandidate: RecoveryProviderId;
  checkedAt: string;
  evidence: string[];
  blockers: string[];
  decision: string;
}

export interface RecoveryAttemptInput {
  attemptId: string;
  inputKind: RecoveryInputKind;
  recoveryCredential?: string;
}

export interface RecoveryAttemptResult {
  status: RecoveryAttemptStatus;
  reason: string;
  recoveredWalletAddress?: string;
  deterministicAppKeysProved: boolean;
}

export interface RecoveryProvider {
  id: RecoveryProviderId;
  label: string;
  getCapability(): RecoveryProviderCapability;
  getSpikeRecord(now?: Date): RecoverySpikeRecord;
  recoverIdentity(input: RecoveryAttemptInput): Promise<RecoveryAttemptResult>;
}

export const RECOVERY_SECRET_FIELD_RE = /(phrase|mnemonic|private.?key|derived.?key|recovery.?credential|recovery.?key|provider.?token|access.?token|secret)/i;

export function createDeferredRecoveryProvider(): RecoveryProvider {
  return {
    id: 'unsupported',
    label: 'Recovery deferred',
    getCapability: () => ({
      providerId: 'unsupported',
      inputKind: 'none',
      phraseBased: false,
      canRecoverWalletIdentity: false,
      canProveDeterministicAppKeys: false,
      notes: [
        'Provider boundary exists, but no fresh-device identity and deterministic app-key round trip has been proved.',
        'Current UI must not expose phrase import or mock success.',
      ],
    }),
    getSpikeRecord: createRecoverySplitDeferRecord,
    recoverIdentity: async () => ({
      status: 'deferred',
      reason: 'Recovery provider proof is pending; Slice 07 split/deferred Recover rather than shipping a mock flow.',
      deterministicAppKeysProved: false,
    }),
  };
}

export function createRecoverySplitDeferRecord(now = new Date()): RecoverySpikeRecord {
  return {
    contractVersion: RECOVERY_PROVIDER_CONTRACT_VERSION,
    status: 'split-deferred',
    providerCandidate: 'privy',
    checkedAt: now.toISOString(),
    evidence: [
      '@privy-io/react-auth@3.14.1 exposes recovery-related hooks, including useRecoverEmbeddedWallet and useSetWalletRecovery.',
      'packages/identity PrivyAdapter only exposes wallet conversion and WalletSigner creation; it has no provider-agnostic recovery/export/import method.',
      'OwnYou app-key recovery depends on proving the recovered wallet can reproduce deterministic key material on a fresh device.',
    ],
    blockers: [
      'No authenticated fresh-device Privy round trip was executed in this implementation pass.',
      'No proof yet that Privy recovery restores both wallet identity and OwnYou deterministic app keys for the same user.',
      'No phrase-based primitive is proved; UI must not hardcode 12-word or 24-word recovery copy.',
    ],
    decision: 'Split/defer Recover. Settings and AuditLog may proceed with an honest non-success recovery state and no mock recovery route.',
  };
}

export function assertBodySafeRecoveryRecord(value: unknown): void {
  const violations: string[] = [];
  scanForRecoverySecrets(value, '$', violations);
  if (violations.length > 0) {
    throw new Error(`Recovery record contains forbidden secret material: ${violations.join(', ')}`);
  }
}

function scanForRecoverySecrets(value: unknown, path: string, violations: string[]): void {
  if (value == null) return;
  if (Array.isArray(value)) {
    value.forEach((item, index) => scanForRecoverySecrets(item, `${path}[${index}]`, violations));
    return;
  }
  if (typeof value !== 'object') return;

  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const childPath = `${path}.${key}`;
    if (RECOVERY_SECRET_FIELD_RE.test(key)) {
      violations.push(childPath);
      continue;
    }
    scanForRecoverySecrets(child, childPath, violations);
  }
}
